import ActionTypes from './ActionTypes';
import AppDispatcher from './AppDispatcher';
import WebApi from './web/WebApi';
import ErrorHelper from './web/ErrorHelper';
import LocalStorage from './utils/LocalStorage';
import LandPageTypes from './views/LandPageTypes';

const WebApiUtils = {
    loginAsync(username, password) {
        AppDispatcher.dispatch({
            type: ActionTypes.LOGIN_START,
            username,
            password,
        });

        return WebApi
            .login(username, password)
            .then(function (result) {
                LocalStorage
                    .updateUser(result.user)
                    .updateToken(result.token)
                    .updateLandPage(LandPageTypes.ADMIN)
                    .saveState();

                AppDispatcher.dispatch({
                    type: ActionTypes.LOGIN_FINISH,
                    user: result.user,
                    token: result.token,
                });

                return {
                    token: result.token,
                    user: result.user
                };
            })
            .catch(function (error) {
                AppDispatcher.dispatch({
                    type: ActionTypes.LOGIN_FAILED,
                    error
                });

                return Promise.reject(error);
            });
    },

    verifyCpfAsync(cpf, event, token) {
        AppDispatcher.dispatch({
            type: ActionTypes.VERIFY_CPF_START,
            cpf,
            event,
            token
        });

        return WebApi
            .verifyCpf(cpf, event, token)
            .then(participant => {
                AppDispatcher.dispatch({
                    type: ActionTypes.VERIFY_CPF_FINISH,
                    participant
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.verifyCpfAsync(cpf, event, newToken));
                } else {
                    if (ErrorHelper.isServerError(error, 404, 'not_found', ['Participant'])) {
                        AppDispatcher.dispatch({
                            type: ActionTypes.VERIFY_CPF_NOT_FOUND
                        });
                    } else {
                        AppDispatcher.dispatch({
                            type: ActionTypes.VERIFY_CPF_FAILED,
                            error
                        });
                    }

                    return Promise.reject(error);
                }
            });
    },

    printBadgeAsync(usualName, participant, event, token) {
        AppDispatcher.dispatch({
            type: ActionTypes.PRINT_BADGE_START,
            participant, event, token, usualName
        });

        return WebApi
            .printBadge(usualName, participant, event, token)
            .then(participant => {
                AppDispatcher.dispatch({
                    type: ActionTypes.PRINT_BADGE_FINISH,
                    participant
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.printBadgeAsync(usualName, participant, event, newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.PRINT_BADGE_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    linkCoursewareAsync(participant, event, coursewareId, token) {
        AppDispatcher.dispatch({
            type: ActionTypes.LINK_COURSEWARE_START,
            participant, event, coursewareId, token
        });

        return WebApi
            .linkCourseware(participant, event, coursewareId, token)
            .then(participant => {
                AppDispatcher.dispatch({
                    type: ActionTypes.LINK_COURSEWARE_FINISH,
                    participant
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.linkCourseware(participant, event, coursewareId, newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.LINK_COURSEWARE_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    printTicketAsync(course, coursewareIds, token) {
        AppDispatcher.dispatch({
            type: ActionTypes.PRINT_TICKET_START,
            coursewareIds
        });

        return WebApi
            .printTickets(course, coursewareIds, token)
            .then(filename => {
                AppDispatcher.dispatch({
                    type: ActionTypes.PRINT_TICKET_FINISH,
                    filename
                });

                return { token, filename };
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.printTicketAsync(coursewareIds, newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.PRINT_TICKET_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectCountriesAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_COUNTRIES_START,
            token
        });

        return WebApi
            .selectCountries(token)
            .then(countries => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_COUNTRIES_FINISH,
                    countries
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectCountriesAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_COUNTRIES_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectCitiesAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_CITIES_START,
            token
        });

        return WebApi
            .selectCities(token)
            .then(cities => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_CITIES_FINISH,
                    cities
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectCitiesAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_CITIES_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectCoursesAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_COURSES_START,
            token
        });

        return WebApi
            .selectCourses(token)
            .then(courses => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_COURSES_FINISH,
                    courses
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectCoursesAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_COURSES_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectCentersAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_CENTERS_START,
            token
        });

        return WebApi
            .selectCenters(token)
            .then(centers => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_CENTERS_FINISH,
                    centers
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectCentersAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_CENTERS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectProductCategoriesAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_PRODUCT_CATEGORIES_START,
            token
        });

        return WebApi
            .selectProductCategories(token)
            .then(categories => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_PRODUCT_CATEGORIES_FINISH,
                    categories
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectProductCategoriesAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_PRODUCT_CATEGORIES_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectEventsAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_EVENTS_START,
            token
        });

        return WebApi
            .selectEvents(token)
            .then(events => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_EVENTS_FINISH,
                    events
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectEventsAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_EVENTS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectAttendanceColumnsAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_ATTENDANCE_COLUMNS_START,
            token
        });

        return WebApi
            .selectAttendanceColumns(token)
            .then(columns => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_ATTENDANCE_COLUMNS_FINISH,
                    columns
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectAttendanceColumnsAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_ATTENDANCE_COLUMNS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectPermissionsAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_PERMISSIONS_START,
            token
        });

        return WebApi
            .selectPermissions(token)
            .then(permissions => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_PERMISSIONS_FINISH,
                    permissions
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectPermissionsAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_PERMISSIONS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectProfilesAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_PROFILES_START,
            token
        });

        return WebApi
            .selectProfiles(token)
            .then(profiles => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_PROFILES_FINISH,
                    profiles
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectProfilesAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_PROFILES_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectUsersAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_USERS_START,
            token
        });

        return WebApi
            .selectUsers(token)
            .then(users => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_USERS_FINISH,
                    users
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectUsersAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_USERS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectEventAsync(id, token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_EVENT_START,
            token
        });

        return WebApi
            .selectEvent(id, token)
            .then(event => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_EVENT_FINISH,
                    event
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectEventAsync(id, newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_EVENT_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    listDashboardAsync(token, eventId) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_RELATORIO_DASHBOARD_START,
            token
        });

        return WebApi
            .listDashboard(token, eventId)
            .then(report => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_RELATORIO_DASHBOARD_FINISH,
                    report
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.listDashboardAsync(newToken, eventId));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_RELATORIO_DASHBOARD_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    listVendasTotaisAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_VENDAS_TOTAIS_START,
            token
        });

        return WebApi
            .listVendasTotais(token)
            .then(report => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_VENDAS_TOTAIS_FINISH,
                    report
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.listVendasTotaisAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_VENDAS_TOTAIS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    listVendasDosNucleosAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_VENDAS_DOS_NUCLEOS_START,
            token
        });

        return WebApi
            .listVendasDosNucleos(token)
            .then(report => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_VENDAS_DOS_NUCLEOS_FINISH,
                    report
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.listVendasDosNucleosAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_VENDAS_DOS_NUCLEOS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    listVendasPorNucleosAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_VENDAS_POR_NUCLEOS_START,
            token
        });

        return WebApi
            .listVendasPorNucleos(token)
            .then(report => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_VENDAS_POR_NUCLEOS_FINISH,
                    report
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.listVendasPorNucleosAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_VENDAS_POR_NUCLEOS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    listVendasPorProdutosAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_VENDAS_POR_PRODUTOS_START,
            token
        });

        return WebApi
            .listVendasPorProdutos(token)
            .then(report => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_VENDAS_POR_PRODUTOS_FINISH,
                    report
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.listVendasPorProdutosAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_VENDAS_POR_PRODUTOS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    getSelectedEventAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.GET_SELECTED_EVENT_START,
            token
        });

        return WebApi
            .getSelectedEvent(token)
            .then(event => {
                AppDispatcher.dispatch({
                    type: ActionTypes.GET_SELECTED_EVENT_FINISH,
                    event,
                });

                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.getSelectedEventAsync(newToken));
                } else {
                    if (error.status === 404) {
                        AppDispatcher.dispatch({
                            type: ActionTypes.GET_SELECTED_EVENT_FINISH,
                            event: null,
                        });

                        return null;
                    } else {
                        AppDispatcher.dispatch({
                            type: ActionTypes.GET_SELECTED_EVENT_FAILED,
                            error
                        });

                        return Promise.reject(error);
                    }
                }
            });
    },

    updateTokenAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.UPDATE_TOKEN_START,
            token,
        });

        return WebApi
            .updateToken(token)
            .then(newToken => {
                LocalStorage
                    .updateToken(newToken)
                    .saveState();

                AppDispatcher.dispatch({
                    type: ActionTypes.UPDATE_TOKEN_FINISH,
                    token: newToken
                });

                return newToken;
            })
            .catch(error => {
                AppDispatcher.dispatch({
                    type: ActionTypes.UPDATE_TOKEN_FAILED,
                    error
                });

                return Promise.reject(token);
            });
    },

    revalidateToken(token) {
        return WebApi
            .updateToken(token)
            .then(newToken => {
                return newToken;
            });
    },

    importSheetAsync(file, event, token) {
        AppDispatcher.dispatch({
            type: ActionTypes.IMPORT_SHEET_START,
            token
        });

        function internalImportSheetAsync(sheet, event, token) {
            return WebApi
                .importSheet(sheet, event, token)
                .then(result => {
                    AppDispatcher.dispatch({
                        type: ActionTypes.IMPORT_SHEET_FINISH,
                        fails: result.fails,
                    });

                    return token;
                })
                .catch(error => {
                    if (ErrorHelper.isTokenExpired(error)) {
                        return WebApiUtils
                            .updateTokenAsync(token)
                            .then(newToken => internalImportSheetAsync(sheet, event, newToken));
                    } else {
                        AppDispatcher.dispatch({
                            type: ActionTypes.IMPORT_SHEET_FAILED,
                            error
                        });

                        return Promise.reject(error);
                    }
                });
        }

        return new Promise(function (resolve, reject) {
            let reader = new FileReader();

            reader.onload = function (e) {
                resolve(e.target.result);
            };

            reader.onerror = function (e) {
                reject(e);
            };

            reader.readAsDataURL(file);
        }).then(content => {
            return internalImportSheetAsync(content, event, token)
        });
    },

    importSheetProductsAsync(file, event, token) {
        AppDispatcher.dispatch({
            type: ActionTypes.IMPORT_SHEET_PRODUCTS_START,
            token
        });

        function internalImportSheetProductsAsync(sheet, event, token) {
            return WebApi
                .importSheetProducts(sheet, event, token)
                .then(result => {
                    AppDispatcher.dispatch({
                        type: ActionTypes.IMPORT_SHEET_PRODUCTS_FINISH,
                        fails: result.fails,
                    });

                    return token;
                })
                .catch(error => {
                    if (ErrorHelper.isTokenExpired(error)) {
                        return WebApiUtils
                            .updateTokenAsync(token)
                            .then(newToken => internalImportSheetProductsAsync(sheet, event, newToken));
                    } else {
                        AppDispatcher.dispatch({
                            type: ActionTypes.IMPORT_SHEET_PRODUCTS_FAILED,
                            error
                        });

                        return Promise.reject(error);
                    }
                });
        }

        return new Promise(function (resolve, reject) {
            let reader = new FileReader();

            reader.onload = function (e) {
                resolve(e.target.result);
            };

            reader.onerror = function (e) {
                reject(e);
            };

            reader.readAsDataURL(file);
        }).then(content => {
            return internalImportSheetProductsAsync(content, event, token)
        });
    },

    listProductsAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.LIST_PRODUCTS_START,
            token
        });

        return WebApi
            .listProducts(token)
            .then(produtos => {
                AppDispatcher.dispatch({
                    type: ActionTypes.LIST_PRODUCTS_FINISH,
                    produtos
                });
                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.listProductsAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.LIST_PRODUCTS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    selectProductsAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.SELECT_PRODUCTS_START,
            token
        });

        return WebApi
            .selectProducts(token)
            .then(result => {
                AppDispatcher.dispatch({
                    type: ActionTypes.SELECT_PRODUCTS_FINISH,
                    produtos : result.data,
                });
                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.selectProductsAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.SELECT_PRODUCTS_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    createCartAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.CREATE_CART_START,
            token
        });

        return WebApi
            .createCart(token)
            .then(result => {
                AppDispatcher.dispatch({
                    type: ActionTypes.CREATE_CART_FINISH,
                    id: result.id
                });
                return {token, id: result.id};
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.createCartAsync(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.CREATE_CART_FAILED,
                        error
                    });

                    return Promise.reject(error);
                }
            });
    },

    listCartItemsAsync(cartId, token) {
      AppDispatcher.dispatch({
          type: ActionTypes.LIST_CART_ITEMS_START,
          token
      });

      return WebApi
          .listCartItems(cartId, token)
          .then(products => {
              AppDispatcher.dispatch({
                  type: ActionTypes.LIST_CART_ITEMS_FINISH,
                  itens: products
              });
              return token;
          })
          .catch(error => {
              if (ErrorHelper.isTokenExpired(error)) {
                  return WebApiUtils
                      .updateTokenAsync(token)
                      .then(newToken => WebApiUtils.listCartItemsAsync(cartId, newToken));
              } else {
                  AppDispatcher.dispatch({
                      type: ActionTypes.LIST_CART_ITEMS_FAILED,
                      error
                  });

                  return Promise.reject(error);
              }
          });
    },

    updateCartItemAsync(cartId, update, token) {
      AppDispatcher.dispatch({
          type: ActionTypes.UPDATE_CART_START,
          token
      });

      return WebApi
          .updateCartItem(cartId, update, token)
          .then(result => {
              AppDispatcher.dispatch({
                  type: ActionTypes.UPDATE_CART_FINISH,
                  id: result.id
              });
              return token;
          })
          .catch(error => {
              if (ErrorHelper.isTokenExpired(error)) {
                  return WebApiUtils
                      .updateTokenAsync(token)
                      .then(newToken => WebApiUtils.updateCartItemAsync(cartId, update, newToken));
              } else {
                  AppDispatcher.dispatch({
                      type: ActionTypes.UPDATE_CART_FAILED,
                      error
                  });

                  return Promise.reject(error);
              }
          });
    },

    closeCartAsync(cartId, clientId, token) {
      AppDispatcher.dispatch({
          type: ActionTypes.CLOSE_CART_START,
          token
      });

      return WebApi
          .closeCart(cartId, clientId, token)
          .then(result => {
              AppDispatcher.dispatch({
                  type: ActionTypes.CLOSE_CART_FINISH,
                  result
              });
              return token;
          })
          .catch(error => {
              if (ErrorHelper.isTokenExpired(error)) {
                  return WebApiUtils
                      .updateTokenAsync(token)
                      .then(newToken => WebApiUtils.closeCartAsync(cartId, clientId, newToken));
              } else {
                  AppDispatcher.dispatch({
                      type: ActionTypes.CLOSE_CART_FAILED,
                      error
                  });
                  return Promise.reject(error);
              }
          });
    },

    deleteCartAsync(cartId, token) {
      AppDispatcher.dispatch({
          type: ActionTypes.DELETE_CART_START,
          token
      });

      return WebApi
          .deleteCart(cartId, token)
          .then(result => {
              AppDispatcher.dispatch({
                  type: ActionTypes.DELETE_CART_FINISH,
                  result,
                  id: cartId
              });
              return token;
          })
          .catch(error => {
              if (ErrorHelper.isTokenExpired(error)) {
                  return WebApiUtils
                      .updateTokenAsync(token)
                      .then(newToken => WebApiUtils.deleteCartAsync(cartId, newToken));
              } else {
                  AppDispatcher.dispatch({
                      type: ActionTypes.DELETE_CART_FAILED,
                      error
                  });
                  return Promise.reject(error);
              }
          });
    },

    listOpenOrdersAsync(token) {
      AppDispatcher.dispatch({
          type: ActionTypes.LIST_OPEN_ORDERS_START,
          token
      });

      return WebApi
          .listOpenOrders(token)
          .then(orders => {
              AppDispatcher.dispatch({
                  type: ActionTypes.LIST_OPEN_ORDERS_FINISH,
                  itens: orders
              });
              return token;
          })
          .catch(error => {
              if (ErrorHelper.isTokenExpired(error)) {
                  return WebApiUtils
                      .updateTokenAsync(token)
                      .then(newToken => WebApiUtils.listOpenOrdersAsync(newToken));
              } else {
                  AppDispatcher.dispatch({
                      type: ActionTypes.LIST_OPEN_ORDERS_FAILED,
                      error
                  });

                  return Promise.reject(error);
              }
          });
    },

    listPaymentMethodsAsync(cartId, token) {
      AppDispatcher.dispatch({
          type: ActionTypes.LIST_PAYMENT_METHODS_START,
          token
      });

      return WebApi
          .listPaymentMethods(cartId, token)
          .then(methods => {
              AppDispatcher.dispatch({
                  type: ActionTypes.LIST_PAYMENT_METHODS_FINISH,
                  methods
              });
              return token;
          })
          .catch(error => {
              if (ErrorHelper.isTokenExpired(error)) {
                  return WebApiUtils
                      .updateTokenAsync(token)
                      .then(newToken => WebApiUtils.listPaymentMethodsAsync(cartId, newToken));
              } else {
                  AppDispatcher.dispatch({
                      type: ActionTypes.LIST_PAYMENT_METHODS_FAILED,
                      error
                  });

                  return Promise.reject(error);
              }
          });
    },

    closeOrderAsync(orderId, payment, token) {
      AppDispatcher.dispatch({
          type: ActionTypes.CLOSE_ORDER_START,
          token
      });

      return WebApi
          .closeOrder(orderId, payment, token)
          .then(result => {
              AppDispatcher.dispatch({
                  type: ActionTypes.CLOSE_ORDER_FINISH,
                  result,
                  orderId,
              });
              return token;
          })
          .catch(error => {
              if (ErrorHelper.isTokenExpired(error)) {
                  return WebApiUtils
                      .updateTokenAsync(token)
                      .then(newToken => WebApiUtils.closeOrderAsync(orderId, payment, newToken));
              } else {
                  AppDispatcher.dispatch({
                      type: ActionTypes.CLOSE_ORDER_FAILED,
                      error
                  });
                  return Promise.reject(error);
              }
          });
    },

    printOrderReceiptAsync(orderId, token) {
        AppDispatcher.dispatch({
          type: ActionTypes.PRINT_ORDER_RECEIPT_START,
          token
        });

        return WebApi
            .printOrderReceipt(orderId, token)
            .then(result => {
                AppDispatcher.dispatch({
                    type: ActionTypes.PRINT_ORDER_RECEIPT_FINISH,
                    result
                });
                return token;
            })
        .catch(error => {
            if (ErrorHelper.isTokenExpired(error)) {
                return WebApiUtils
                    .updateTokenAsync(token)
                    .then(newToken => WebApiUtils.printOrderReceiptAsync(orderId, newToken));
            } else {
                AppDispatcher.dispatch({
                    type: ActionTypes.PRINT_ORDER_RECEIPT_FAILED,
                    error
                });
                return Promise.reject(error);
            }
        });
    },

    printOrderDeliveryAsync(orderId, token) {
        AppDispatcher.dispatch({
          type: ActionTypes.PRINT_ORDER_DELIVERY_START,
          token
        });

        return WebApi
            .printOrderDelivery(orderId, token)
            .then(result => {
                AppDispatcher.dispatch({
                    type: ActionTypes.PRINT_ORDER_DELIVERY_FINISH,
                    result
                });
                return token;
            })
        .catch(error => {
            if (ErrorHelper.isTokenExpired(error)) {
                return WebApiUtils
                    .updateTokenAsync(token)
                    .then(newToken => WebApiUtils.printOrderDeliveryAsync(orderId, newToken));
            } else {
                AppDispatcher.dispatch({
                    type: ActionTypes.PRINT_ORDER_DELIVERY_FAILED,
                    error
                });
                return Promise.reject(error);
            }
        });
    },

    listClosedOrdersAsync(token) {
      AppDispatcher.dispatch({
          type: ActionTypes.LIST_CLOSED_ORDERS_START,
          token
      });

      return WebApi
          .listClosedOrders(token)
          .then(orders => {
              AppDispatcher.dispatch({
                  type: ActionTypes.LIST_CLOSED_ORDERS_FINISH,
                  itens: orders
              });
              return token;
          })
          .catch(error => {
              if (ErrorHelper.isTokenExpired(error)) {
                  return WebApiUtils
                      .updateTokenAsync(token)
                      .then(newToken => WebApiUtils.listClosedOrdersAsync(newToken));
              } else {
                  AppDispatcher.dispatch({
                      type: ActionTypes.LIST_CLOSED_ORDERS_FAILED,
                      error
                  });

                  return Promise.reject(error);
              }
          });
    },

    deliverOrderAsync(orderId, token) {
      AppDispatcher.dispatch({
          type: ActionTypes.DELIVER_ORDER_START,
          token
      });

      return WebApi
          .deliverOrder(orderId, token)
          .then(result => {
              AppDispatcher.dispatch({
                  type: ActionTypes.DELIVER_ORDER_FINISH,
                  result,
                  orderId,
              });
              return token;
          })
          .catch(error => {
              if (ErrorHelper.isTokenExpired(error)) {
                  return WebApiUtils
                      .updateTokenAsync(token)
                      .then(newToken => WebApiUtils.deliverOrderAsync(orderId, newToken));
              } else {
                  AppDispatcher.dispatch({
                      type: ActionTypes.DELIVER_ORDER_FAILED,
                      error
                  });
                  return Promise.reject(error);
              }
          });
    },

    findClientNameAsync(cpf, token) {
      AppDispatcher.dispatch({
          type: ActionTypes.FIND_CLIENT_NAME_START,
          token
      });

      return WebApi
          .findClientName(cpf, token)
          .then(result => {
              AppDispatcher.dispatch({
                  type: ActionTypes.FIND_CLIENT_NAME_FINISH,
                  name: result.name,
              });
              return token;
          })
          .catch(error => {
              if (ErrorHelper.isTokenExpired(error)) {
                  return WebApiUtils
                      .updateTokenAsync(token)
                      .then(newToken => WebApiUtils.findClientNameAsync(cpf, newToken));
              } else {
                  AppDispatcher.dispatch({
                      type: ActionTypes.FIND_CLIENT_NAME_FAILED,
                      error
                  });
                  return Promise.reject(error);
              }
          });
    },

    listPaidOrdersAsync(token) {
        AppDispatcher.dispatch({
            type: ActionTypes.LIST_PAID_ORDERS_START,
            token
        });

        return WebApi
            .listPaidOrders(token)
            .then(orders => {
                AppDispatcher.dispatch({
                    type: ActionTypes.LIST_PAID_ORDERS_FINISH,
                    itens: orders
                });
                return {token, orders: orders};
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.listPaidOrders(newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.LIST_PAID_ORDERS_FAILED,
                        error
                    });
                    return Promise.reject(error);
                }
            });
    },

    listParticipantsAsync(eventId, token) {
        AppDispatcher.dispatch({
            type: ActionTypes.LIST_PARTICIPANTS_START,
            token
        });

        return WebApi
            .listParticipants(eventId, token)
            .then(result => {
                AppDispatcher.dispatch({
                    type: ActionTypes.LIST_PARTICIPANTS_FINISH,
                    participants: result.data,
                });
                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.listParticipants(eventId, newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.LIST_PARTICIPANTS_FAILED,
                        error
                    });
                    return Promise.reject(error);
                }
            });
    },

    listAmountBySlotAsync(token, eventId, from, to) {
        AppDispatcher.dispatch({
            type: ActionTypes.LIST_AMOUNT_BY_SLOT_START,
            token
        });

        return WebApi
            .listAmountBySlot(token, eventId, from, to)
            .then(result => {
                AppDispatcher.dispatch({
                    type: ActionTypes.LIST_AMOUNT_BY_SLOT_FINISH,
                    result,
                    eventId,
                });
                return token;
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.listAmountBySlot(newToken, eventId, from, to));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.LIST_AMOUNT_BY_SLOT_FAILED,
                        error
                    });
                    return Promise.reject(error);
                }
            });
    },

    createLotteryGameAsync(eventId, token) {
        AppDispatcher.dispatch({
            type: ActionTypes.CREATE_LOTTERY_GAME_START,
            token
        });

        return WebApi
            .createLotteryGame(eventId, token)
            .then(result => {
                AppDispatcher.dispatch({
                    type: ActionTypes.CREATE_LOTTERY_GAME_FINISH,
                    id: result.id
                });
                return {token, id: result.id};
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.createLotteryGameAsync(eventId, newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.CREATE_LOTTERY_GAME_FAILED,
                        error
                    });
                    return Promise.reject(error);
                }
            });
    },

    updateLotteryGameAsync(gameId, winners, criteria, token) {
        AppDispatcher.dispatch({
            type: ActionTypes.UPDATE_LOTTERY_GAME_START,
            token
        });

        return WebApi
            .updateLotteryGame(gameId, winners, criteria, token)
            .then(result => {
                AppDispatcher.dispatch({
                    type: ActionTypes.UPDATE_LOTTERY_GAME_FINISH,
                    id: result.id
                });
                return {token, id: result.id};
            })
            .catch(error => {
                if (ErrorHelper.isTokenExpired(error)) {
                    return WebApiUtils
                        .updateTokenAsync(token)
                        .then(newToken => WebApiUtils.updateLotteryGameAsync(gameId, winners, criteria, newToken));
                } else {
                    AppDispatcher.dispatch({
                        type: ActionTypes.UPDATE_LOTTERY_GAME_FAILED,
                        error
                    });
                    return Promise.reject(error);
                }
            });
    },
};

export default WebApiUtils;



// WEBPACK FOOTER //
// src/WebApiUtils.js